import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import AutoScrollPlugin from '../plugins/AutoScrollPlugin';
import MetronomePlugin from '../plugins/MetronomePlugin';
import ServiceProgressBar from '../ServiceProgressBar';
import SongListModal from '../SongListModal';

import { useGlobalContext } from '../../../../context/GlobalContext';
import useSongChangeMetronome from '../plugins/MetronomePlugin/useSongChangeMetronome';
import { stopScroll } from '../../../../utils/autoScrollHelpers';
import { getSetting, settingKeys } from '../../../../utils/settings';

import Icon from '../../../../components/common/Icon';
import { ReactComponent as ArrowBackIcon } from '../../../../icons/arrow-left-circle-solid.svg';
import { ReactComponent as ArrowForwardIcon } from '../../../../icons/arrow-right-circle-solid.svg';

const ViewServiceBar = ({ onPrevious, onNext, firstSong, lastSong, service, continuousView, songs, song, badges }) => {
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [isPinned, setIsPinned] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasBadges, setHasBadges] = useState(true);
  const rowRef = useRef(null);
  const handleMetronomeOnSongChange = useSongChangeMetronome();

  const renderPreviousClass = (firstSong) => (firstSong ? 'no-action' : 'clickable');
  const renderNextClass = (lastSong) => (lastSong ? 'no-action' : 'clickable');

  const { isScrolling, scrollTimerRef, setIsScrolling, showPlugins } = useGlobalContext();

  const handleOpenModal = () => {
    stopScroll(scrollTimerRef, setIsScrolling);
    setIsModalOpen(true);
  };

  // SongBadges renders nothing when a song has none, so whether it did is read off the row itself
  useLayoutEffect(() => {
    setHasBadges(Boolean(rowRef.current?.querySelector('.SongBadges')));
  }, [badges]);

  // Outside a service, a song with no badges has nothing for the row to show, so it isn't laid out
  const hasRow = Boolean(service) || hasBadges;

  // The bar comes down once the last piece of the header has scrolled off the top: the row, or with
  // no row the plugins when they're open, or else the app bar. The row is watched by what it holds,
  // the arrows or else the badges, since its padding runs on below them.
  useEffect(() => {
    const el = hasRow
      ? rowRef.current?.querySelector(service ? '.view__service-bar__action-icons' : '.SongBadges')
      : document.querySelector(showPlugins ? '.ViewSongPlugins' : '.NavBar');

    if (!el) return;

    const observer = new IntersectionObserver(([e]) => setIsPinned(!e.isIntersecting && e.boundingClientRect.top < 0));

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [hasRow, showPlugins, service]);

  // Track current song position in continuous view
  useEffect(() => {
    if (!continuousView || !songs || songs.length === 0) {
      setCurrentSongIndex(0);
      return;
    }

    let isMounted = true; // Track if component is still mounted

    const updateCurrentSong = () => {
      if (!isMounted) return;

      const songSections = document.querySelectorAll('.continuous-view__song-section');
      if (songSections.length === 0) return;

      const headerOffset = 120; // Account for sticky header
      let newCurrentIndex = 0;

      // Find the last song that has been fully scrolled past (100% complete)
      // or the first song that is currently being scrolled through
      songSections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        const sectionTop = rect.top;
        const sectionHeight = rect.height;
        const sectionBottom = sectionTop + sectionHeight;

        // If section has been completely scrolled past (bottom is at or above header)
        if (sectionBottom <= headerOffset) {
          // This song is complete, next song should be active
          newCurrentIndex = Math.min(index + 1, songs.length - 1);
        }
        // If section top is above header but bottom is below (currently scrolling through)
        else if (sectionTop < headerOffset && sectionBottom > headerOffset) {
          // This is the active song
          newCurrentIndex = index;
        }
        // If we haven't reached this section yet, keep the current index
      });

      // Handle metronome when moving to a new song
      if (newCurrentIndex !== currentSongIndex) {
        handleMetronomeOnSongChange(songs[newCurrentIndex]);
      }

      setCurrentSongIndex(newCurrentIndex);
    };

    // Initial check
    updateCurrentSong();

    // Update on scroll with throttling
    let rafId = null;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        updateCurrentSong();
        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      isMounted = false; // Mark as unmounted
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [continuousView, songs, currentSongIndex, handleMetronomeOnSongChange]);

  const scrollStatus = () => {
    return isScrolling;
  };

  // Scroll to specific song in continuous view
  const scrollToSong = (direction) => {
    if (!continuousView || !songs) return;

    const targetIndex = direction === 'next' ? currentSongIndex + 1 : currentSongIndex - 1;

    if (targetIndex < 0 || targetIndex >= songs.length) return;

    scrollToSongSection(targetIndex);
  };

  const handlePrevious = () => {
    // Don't navigate if auto-scrolling is active
    if (scrollStatus()) return;

    if (continuousView) {
      scrollToSong('previous');
    } else {
      onPrevious();
    }
  };

  const scrollToSongSection = (index) => {
    // First song - scroll to top of page
    if (index === 0) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      return;
    }

    // Subsequent songs - scroll to the song's header within its section
    const songSections = document.querySelectorAll('.continuous-view__song-section');
    const targetSection = songSections[index];

    if (targetSection) {
      const targetElement = targetSection.querySelector('.SongHeaderSection');

      if (targetElement) {
        // Get the actual height of the sticky service bar
        const serviceBar = document.querySelector('.view__service-bar');
        const serviceBarHeight = serviceBar ? serviceBar.offsetHeight : 0;

        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - serviceBarHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }
  };

  const handleNext = () => {
    // Don't navigate if auto-scrolling is active
    if (scrollStatus()) return;

    if (continuousView) {
      scrollToSong('next');
    } else {
      onNext();
    }
  };

  const handleSongNavigation = (direction) => {
    if (direction === 'previous') {
      handlePrevious();
    } else {
      handleNext();
    }
  };

  // Left/right arrows page through the service, doing exactly what the prev/next buttons do —
  // including the continuous-view scroll and the block while auto-scrolling. Bound here rather
  // than in View because this is where both modes are already resolved to one navigation call.
  useEffect(() => {
    if (!service) return;

    const handleArrowKeys = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      // Cmd/Ctrl+Arrow is browser history, and Alt/Shift+Arrow is text selection
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;

      // Don't hijack the keys from anything that uses them itself: a field being typed in, a
      // slider being nudged, or an open menu or modal sitting on top of the song
      const { tagName, isContentEditable } = e.target;
      if (isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName)) return;
      if (document.querySelector('.Modal, .mg-dropdown__menu')) return;

      e.preventDefault();
      handleSongNavigation(e.key === 'ArrowLeft' ? 'previous' : 'next');
    };

    document.addEventListener('keydown', handleArrowKeys);
    return () => document.removeEventListener('keydown', handleArrowKeys);
    // No dependency array on purpose: the navigation handler closes over the song position and
    // the scrolling flag, both of which move while the service is being played, so the listener
    // is rebound each render rather than firing against a stale one.
  });

  const getPreviousButtonClass = () => {
    // Disable if scrolling
    if (scrollStatus()) return 'no-action';

    if (continuousView && songs) {
      return currentSongIndex === 0 ? 'no-action' : 'clickable';
    }
    return renderPreviousClass(firstSong);
  };

  const getNextButtonClass = () => {
    // Disable if scrolling
    if (scrollStatus()) return 'no-action';

    if (continuousView && songs) {
      return currentSongIndex >= songs.length - 1 ? 'no-action' : 'clickable';
    }
    return renderNextClass(lastSong);
  };

  // Get the current song for single song view
  const displaySong = !service && song ? song : songs?.[0];

  // Prepare songs array for progress bar: use service songs if available, otherwise wrap single song
  const progressBarSongs = songs && songs.length > 0 ? songs : song ? [song] : [];

  const superScrollOn = getSetting(settingKeys.superScroll);
  const superMetronomeOn = getSetting(settingKeys.superMetronome);
  // When both floating controls are active the pinned bar has nothing left to show
  // but the prev/next buttons, so we drop its background and keep only
  // the progress bar plus a single rounded container around the buttons.
  const bareBar = superScrollOn && superMetronomeOn;

  // The song the compact controls follow: in continuous view, the one being scrolled through
  const pinnedSong = service && continuousView && songs ? songs[currentSongIndex] : song;

  const actionIcons = service && (
    <span className="view__service-bar__action-icons">
      {continuousView && songs && (
        <span className="view__service-bar__song-position" onClick={handleOpenModal}>
          {currentSongIndex + 1}/{songs.length}
        </span>
      )}
      {!continuousView && (
        <>
          <span className={getPreviousButtonClass()}>
            <Icon icon={<ArrowBackIcon />} className="previous-song-icon solid" onClick={handlePrevious} />
          </span>
          <span className={getNextButtonClass()}>
            <Icon icon={<ArrowForwardIcon />} className="next-song-icon solid" onClick={handleNext} />
          </span>
        </>
      )}
    </span>
  );

  return (
    <>
      <div ref={rowRef} className={cx('view__service-row', { 'view__service-row--empty': !hasRow })}>
        {(service || displaySong) && <span className="view__service-bar__title">{badges}</span>}
        {actionIcons}
      </div>

      <div className={cx('view__service-bar', { pinned: isPinned, 'view__service-bar--bare': bareBar })}>
        {isPinned && service && (
          <>
            {!superScrollOn && <AutoScrollPlugin compact={true} song={pinnedSong} />}
            {!superMetronomeOn && continuousView && pinnedSong && <MetronomePlugin song={pinnedSong} compact={true} />}
            {!superMetronomeOn && !continuousView && song && superScrollOn && (
              <MetronomePlugin song={song} compact={true} />
            )}
          </>
        )}
        {isPinned && !service && displaySong && (
          <>
            {!superScrollOn && <AutoScrollPlugin compact={true} song={displaySong} />}
            {!superMetronomeOn && <MetronomePlugin song={displaySong} compact={true} />}
          </>
        )}
        {actionIcons}
        <ServiceProgressBar songs={progressBarSongs} continuousView={continuousView} pinned={isPinned} />
      </div>

      {continuousView && songs && (
        <SongListModal
          isOpen={isModalOpen}
          onClose={() => {
            stopScroll(scrollTimerRef, setIsScrolling);
            setIsModalOpen(false);
          }}
          title={service.title}
          songs={songs}
          currentSongIndex={currentSongIndex}
          onSongSelect={(index) => {
            scrollToSongSection(index);
            setIsModalOpen(false);
          }}
        />
      )}
    </>
  );
};

ViewServiceBar.propTypes = {
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  firstSong: PropTypes.bool,
  lastSong: PropTypes.bool,
  service: PropTypes.object,
  continuousView: PropTypes.bool,
  songs: PropTypes.array,
  song: PropTypes.object,
  badges: PropTypes.node,
};

export default ViewServiceBar;
